/**
 * pj-collection-pager
 * -------------------
 * Progressive-enhancement pager for the main-collection section.
 *
 * Replaces the earlier <collection-infinite-scroll> prototype. That version
 * worked, but had no device gating, no URL sync, no scroll restoration, and
 * moved the real pagination links into <noscript> (which hides them from
 * Googlebot's rendered view). This element addresses all four.
 *
 * Contract with the Liquid side:
 *   data-section-id    dynamic section id, for ?section_id= fetches
 *   data-devices       "all" | "mobile"  -- when to run infinite loading
 *   data-breakpoint    px; viewports at or below this count as "mobile"
 *   data-auto-loads    pages to auto-load before the Load more button appears
 *   data-current-page  server-rendered page number
 *   data-total-pages   total pages in this result set
 *   data-next-url      URL of the next page, absent on the last page
 *
 * Children:
 *   [data-pj-status]      aria-live region
 *   [data-pj-sentinel]    IntersectionObserver target
 *   [data-pj-more]        Load more button
 *   [data-pj-pagination]  the real, crawlable pagination links
 *
 * The pagination links stay in the DOM at all times and are merely hidden
 * when infinite loading is active, so the paginated series remains crawlable
 * and the no-JS experience is ordinary pagination.
 */
(() => {
  'use strict';

  if (window.__PJ_COLLECTION_PAGER__) return;
  window.__PJ_COLLECTION_PAGER__ = true;

  const STORE_PREFIX = 'pj:collection:';
  const RESTORE_TTL_MS = 30 * 60 * 1000;
  const PRODUCT_LIST_SELECTOR = '.collection__results product-list.product-list';

  class PJCollectionPager extends HTMLElement {
    connectedCallback() {
      this.statusEl = this.querySelector('[data-pj-status]');
      this.sentinel = this.querySelector('[data-pj-sentinel]');
      this.moreBtn = this.querySelector('[data-pj-more]');
      this.paginationEl = this.querySelector('[data-pj-pagination]');
      this.productList = this.closest('.collection')?.querySelector(PRODUCT_LIST_SELECTOR);

      this.nextUrl = this.dataset.nextUrl || '';
      this.sectionId = this.dataset.sectionId || '';
      this.totalPages = Number(this.dataset.totalPages) || 1;
      this.maxLoadedPage = Number(this.dataset.currentPage) || 1;
      this.autoBudget = Number(this.dataset.autoLoads) || 0;
      this.autoUsed = 0;
      this.isLoading = false;
      this.abortController = new AbortController();

      const breakpoint = Number(this.dataset.breakpoint) || 750;
      this.mql = matchMedia(`(max-width: ${breakpoint - 0.02}px)`);
      this.onModeChange = () => this.applyMode();
      this.mql.addEventListener('change', this.onModeChange);

      this.onClick = (event) => this.handleClick(event);
      this.addEventListener('click', this.onClick);

      this.onPageHide = () => this.saveState();
      addEventListener('pagehide', this.onPageHide);

      if (!this.productList) {
        // Nothing to append into: leave native pagination visible and bail.
        return;
      }

      this.observePageMarkers();
      this.applyMode();
      this.restore();
    }

    disconnectedCallback() {
      this.disarmSentinel();
      this.markerObserver?.disconnect();
      this.abortController?.abort();
      this.mql?.removeEventListener('change', this.onModeChange);
      this.removeEventListener('click', this.onClick);
      removeEventListener('pagehide', this.onPageHide);
    }

    /* ---------------------------------------------------------------- mode */

    get infiniteActive() {
      if (this.dataset.devices === 'all') return true;
      return this.dataset.devices === 'mobile' && this.mql.matches;
    }

    get hasMore() {
      return Boolean(this.nextUrl) && this.maxLoadedPage < this.totalPages;
    }

    /**
     * Idempotent. Never removes already-loaded products, so crossing the
     * breakpoint mid-session is lossless in both directions.
     */
    applyMode() {
      const infinite = this.infiniteActive;

      this.paginationEl?.toggleAttribute('hidden', infinite);

      const autoActive = infinite && this.hasMore && this.autoUsed < this.autoBudget;

      if (this.sentinel) this.sentinel.hidden = !autoActive;
      this.moreBtn?.toggleAttribute('hidden', !infinite || !this.hasMore || autoActive);

      if (autoActive) this.armSentinel();
      else this.disarmSentinel();
    }

    armSentinel() {
      if (!this.sentinel) return;

      this.sentinelObserver ??= new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) this.loadNext(false);
        },
        { rootMargin: '600px 0px' }
      );

      this.sentinelObserver.observe(this.sentinel);

      // The observer only fires when intersection STATE changes, and a single
      // frame can jump clean over the trigger zone (End key, find-on-page, an
      // anchor link, a very hard flick). The shopper then sits at the bottom
      // with pagination hidden and no Load more button — a dead end. This
      // scroll check catches the "already past it" case the observer missed.
      this.scrollFallback ??= () => {
        if (this.fallbackRaf) return;
        this.fallbackRaf = requestAnimationFrame(() => {
          this.fallbackRaf = null;
          if (this.sentinel.hidden || this.isLoading) return;
          if (this.sentinel.getBoundingClientRect().top < innerHeight + 600) {
            this.loadNext(false);
          }
        });
      };
      addEventListener('scroll', this.scrollFallback, { passive: true });
    }

    disarmSentinel() {
      this.sentinelObserver?.disconnect();
      if (this.scrollFallback) removeEventListener('scroll', this.scrollFallback);
      if (this.fallbackRaf) {
        cancelAnimationFrame(this.fallbackRaf);
        this.fallbackRaf = null;
      }
    }

    handleClick(event) {
      if (!event.target.closest('[data-pj-more]')) return;
      event.preventDefault();
      this.autoUsed = 0; // an explicit click re-arms the auto budget
      this.loadNext(true);
    }

    /* --------------------------------------------------------------- fetch */

    buildFetchUrl(rawUrl) {
      const url = new URL(rawUrl, location.origin);
      // Carries sort_by, filter.*, and q through untouched.
      if (this.sectionId) url.searchParams.set('section_id', this.sectionId);
      return url;
    }

    async loadNext(fromButton) {
      if (this.isLoading || !this.hasMore) return;
      if (!fromButton) this.autoUsed += 1;

      const targetUrl = this.nextUrl;
      const targetPage = this.maxLoadedPage + 1;

      this.isLoading = true;
      this.setStatus('Loading more products…');
      this.moreBtn?.setAttribute('aria-busy', 'true');

      try {
        const response = await fetch(this.buildFetchUrl(targetUrl), {
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          signal: this.abortController.signal,
        });

        if (!response.ok) throw new Error(`Unable to load collection page (${response.status})`);

        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');

        // ?section_id= returns the bare section, but fall back to the wrapper
        // in case the request was served as a full page.
        const scope =
          doc.querySelector(`#shopify-section-${CSS.escape(this.sectionId)}`) || doc;

        const nextList = scope.querySelector(PRODUCT_LIST_SELECTOR) ||
          scope.querySelector('product-list.product-list');

        if (!nextList) throw new Error('The next collection page did not contain a product grid.');

        const fragment = document.createDocumentFragment();
        const added = [];

        for (const item of Array.from(nextList.children)) {
          // Promo blocks repeat on every paginated response; drop duplicate
          // ids so the resulting document stays valid.
          if (item.id && document.getElementById(item.id)) item.removeAttribute('id');
          item.dataset.pjPage = String(targetPage);
          added.push(item);
          fragment.appendChild(item);
        }

        this.productList.appendChild(fragment);
        this.maxLoadedPage = targetPage;

        const nextPager = scope.querySelector('pj-collection-pager');
        this.nextUrl = nextPager?.dataset.nextUrl || '';
        if (this.nextUrl) this.dataset.nextUrl = this.nextUrl;
        else this.removeAttribute('data-next-url');

        const nextPagination = scope.querySelector('[data-pj-pagination]');
        if (nextPagination && this.paginationEl) {
          this.paginationEl.replaceChildren(...nextPagination.childNodes);
        }

        this.setStatus(`${added.length} more items loaded.`);
        if (added[0] && fromButton) {
          added[0].querySelector('a')?.focus({ preventScroll: true });
        }

        this.observePageMarkers();
        this.applyMode();

        document.dispatchEvent(
          new CustomEvent('pj:collection:page-loaded', {
            detail: { page: targetPage, count: added.length },
          })
        );
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error('pj-collection-pager:', error);
        this.setStatus('More products could not be loaded. Scroll again to retry.');
        this.autoUsed = this.autoBudget; // stop auto-retrying; surface the button
        this.applyMode();
      } finally {
        this.isLoading = false;
        this.moreBtn?.removeAttribute('aria-busy');
      }
    }

    setStatus(message) {
      if (this.statusEl) this.statusEl.textContent = message;
    }

    /* ------------------------------------------------------------ url sync */

    /**
     * Keeps ?page= pointing at the page actually in view, so the URL stays
     * shareable and a reload lands roughly where the shopper was.
     */
    observePageMarkers() {
      if (!this.productList) return;

      this.markerObserver ??= new IntersectionObserver(
        (entries) => {
          const visible = entries.find((entry) => entry.isIntersecting);
          if (!visible) return;

          const page = visible.target.dataset.pjPage || '1';
          const url = new URL(location.href);
          if ((url.searchParams.get('page') || '1') === page) return;

          if (page === '1') url.searchParams.delete('page');
          else url.searchParams.set('page', page);

          history.replaceState(history.state, '', url);
        },
        { rootMargin: '0px 0px -60% 0px' }
      );

      // Only appended pages get tagged during loadNext, so the server-rendered
      // first page had no marker and was never observed: scrolling back to the
      // top left ?page= pointing at whatever page had last come into view.
      const first = this.productList.firstElementChild;
      if (first && !first.dataset.pjPage) {
        first.dataset.pjPage = this.dataset.currentPage || '1';
      }

      for (const item of this.productList.children) {
        const page = item.dataset.pjPage;
        if (!page) continue;
        if (item.previousElementSibling?.dataset.pjPage === page) continue;
        this.markerObserver.observe(item); // first item of each page only
      }
    }

    /* -------------------------------------------------- scroll restoration */

    storeKey() {
      const url = new URL(location.href);
      url.searchParams.delete('page');
      url.searchParams.delete('section_id');
      url.searchParams.sort();
      return `${STORE_PREFIX}${url.pathname}${url.search}`;
    }

    saveState() {
      if (!this.infiniteActive || !this.productList) return;
      if (this.maxLoadedPage <= (Number(this.dataset.currentPage) || 1)) return;

      let anchor = null;
      for (const item of this.productList.children) {
        if (item.getBoundingClientRect().bottom > 0) {
          anchor = item.querySelector('a[href]')?.getAttribute('href') || null;
          break;
        }
      }

      try {
        sessionStorage.setItem(
          this.storeKey(),
          JSON.stringify({ maxPage: this.maxLoadedPage, anchor, y: scrollY, ts: Date.now() })
        );
      } catch {
        /* storage unavailable (private mode / quota) -- restoration is optional */
      }
    }

    async restore() {
      let state = null;
      try {
        state = JSON.parse(sessionStorage.getItem(this.storeKey()));
      } catch {
        return;
      }
      if (!state) return;

      try {
        sessionStorage.removeItem(this.storeKey());
      } catch {
        /* no-op */
      }

      // Only rebuild on a genuine back/forward. A fresh visit or a reload
      // should start at the top with one page, as the server rendered it.
      // (A bfcache restore never reaches here -- the DOM comes back intact.)
      const navEntry = performance.getEntriesByType('navigation')[0];
      if (navEntry?.type !== 'back_forward') return;
      if (!this.infiniteActive) return;
      if (Date.now() - state.ts > RESTORE_TTL_MS) return;

      const previous = history.scrollRestoration;
      history.scrollRestoration = 'manual';

      const target = Math.min(state.maxPage, this.totalPages);
      while (this.maxLoadedPage < target && this.hasMore) {
        const before = this.maxLoadedPage;
        await this.loadNext(true);
        if (this.maxLoadedPage === before) break; // a failed load must not spin
      }

      const anchorEl = state.anchor && this.productList.querySelector(`a[href="${CSS.escape(state.anchor)}"]`);
      if (anchorEl) anchorEl.scrollIntoView({ block: 'center' });
      else if (state.y) scrollTo(0, state.y);

      history.scrollRestoration = previous || 'auto';
    }
  }

  if (!customElements.get('pj-collection-pager')) {
    customElements.define('pj-collection-pager', PJCollectionPager);
  }
})();
